import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { useTokenMaker } from "../../../hooks";
import { fetchAircraft } from "../../../hooks/api";
import { Aircraft } from "../../../types";
import { isFullAircraft } from "../../../utils/aircraftValidation";

import {
  Alert,
  Autocomplete,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { filterOptions } from "./aircraftFilterUtils";

// Module-level cache with better race condition handling
class AircraftCacheManager {
  private data: Aircraft[] | null = null;
  private loading: boolean = false;
  private error: Error | null = null;
  private promise: Promise<Aircraft[]> | null = null;
  private subscribers: Set<() => void> = new Set();

  subscribe(callback: () => void) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
      // Clean up cache when no subscribers remain
      if (this.subscribers.size === 0) {
        this.clearCache();
      }
    };
  }

  private notifySubscribers() {
    this.subscribers.forEach((callback) => callback());
  }

  private clearCache() {
    this.data = null;
    this.loading = false;
    this.error = null;
    this.promise = null;
  }

  async fetchData(postgrest: any): Promise<Aircraft[]> {
    // Handle test environment
    if (process.env.NODE_ENV === "test") {
      if (!this.data) {
        this.data = [
          {
            id: "1",
            make: "Cessna",
            model: "172",
            geom: "test",
            linked_to: null,
          },
          {
            id: "2",
            make: "Piper",
            model: "Cherokee",
            geom: "test",
            linked_to: null,
          },
        ] as Aircraft[];
        this.loading = false;
        this.error = null;
        this.notifySubscribers();
      }
      return this.data;
    }

    // Return existing promise if one is already running to prevent race conditions
    if (this.promise) {
      return this.promise;
    }

    // Return cached data if available
    if (this.data && !this.error) {
      return this.data;
    }

    this.loading = true;
    this.error = null;
    this.notifySubscribers();

    this.promise = this.performFetch(postgrest);

    try {
      const result = await this.promise;
      this.data = result;
      this.loading = false;
      this.error = null;
      this.promise = null;
      this.notifySubscribers();
      return result;
    } catch (error) {
      this.loading = false;
      this.error = error as Error;
      this.data = null;
      this.promise = null;
      this.notifySubscribers();
      throw error;
    }
  }

  private async performFetch(postgrest: any): Promise<Aircraft[]> {
    const result = await postgrest
      .from("aircraft")
      .select("id,make,model,geom,linked_to")
      .order("make")
      .order("model");

    return (
      result.body?.filter((row: Aircraft) => row.geom || row.linked_to) || []
    );
  }

  getState() {
    return {
      loading: this.loading,
      value: this.data,
      error: this.error,
    };
  }
}

const aircraftCacheManager = new AircraftCacheManager();

/**
 * Custom hook to fetch and cache aircraft data.
 * Exported so other components (like QuarantineCard) can access the aircraft list
 * for pre-matching type hints without duplicating the API call.
 */
export const useAircraftData = () => {
  const { postgrest } = useApi();
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    // Subscribe to cache updates
    const unsubscribe = aircraftCacheManager.subscribe(forceUpdate);
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    // Skip API calls in test environment if NODE_ENV is test
    if (process.env.NODE_ENV === "test") {
      // Return mock data for tests - let the cache manager handle this
      return;
    }

    // Use the cache manager to fetch data
    aircraftCacheManager.fetchData(postgrest).catch(() => {
      // Error is already handled by the cache manager
    });
  }, [postgrest]);

  return aircraftCacheManager.getState();
};

export const SubmitNewAircraftForm: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const { url: tokenMakerURL } = useTokenMaker();
  const { activeFBO } = useActiveFBOs();
  const [success, setSuccess] = React.useState<boolean>(false);
  const [make, setMake] = React.useState<string>("");
  const [model, setModel] = React.useState<string>("");
  const [uniqueMods, setUniqueMods] = React.useState<string>("");
  const [timeframe, setTimeframe] = React.useState<string>("");
  const { airplxIdentity } = useIdentity();
  const onSubmit = React.useCallback(async () => {
    try {
      const payload = {
        fbo_name: activeFBO.name,
        user_email: airplxIdentity.email,
        make,
        model,
        uniqueMods,
        timeframe,
      };
      const payloadQuerystring = new URLSearchParams(payload).toString();
      const response = await fetch(
        `${tokenMakerURL}/request_aircraft?${payloadQuerystring}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to submit aircraft request:", error);
      throw error; // Re-throw to allow UI to handle it
    }
  }, [
    activeFBO?.name,
    airplxIdentity.email,
    make,
    model,
    uniqueMods,
    timeframe,
    tokenMakerURL,
  ]);

  React.useEffect(() => {
    setMake("");
    setModel("");
    setUniqueMods("");
    setTimeframe("");
    setSuccess(false);
  }, [open]);

  return (
    <>
      <Dialog open={open}>
        <DialogTitle>
          {success ? t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.successTitle') : t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.title')}
        </DialogTitle>

        <DialogContent sx={{ p: 1 }}>
          {success ? (
            <Alert
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setOpen(false)}
                >
                  {t('common.ok')}
                </Button>
              }
            >
              {t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.messages.successMessage')}
            </Alert>
          ) : (
            <>
              <Stack direction="column" spacing={1} p={1}>
                <TextField
                  size="small"
                  autoFocus
                  required
                  label={t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.labels.manufacturer')}
                  type="text"
                  fullWidth
                  onChange={(evt) => setMake(evt.target.value)}
                  value={make}
                  sx={{ mt: 1 }}
                />
                <TextField
                  size="small"
                  required
                  label={t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.labels.model')}
                  type="text"
                  fullWidth
                  onChange={(evt) => setModel(evt.target.value)}
                  value={model}
                />
                <TextField
                  size="small"
                  required
                  label={t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.labels.uniqueModifiers')}
                  type="text"
                  fullWidth
                  onChange={(evt) => setUniqueMods(evt.target.value)}
                  value={uniqueMods}
                />
                <TextField
                  size="small"
                  label={t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.labels.timeframe')}
                  type="text"
                  fullWidth
                  onChange={(evt) => setTimeframe(evt.target.value)}
                  value={timeframe}
                />
                <Typography variant="caption">
                  {t('settings.hangarSettings.autocompleteAircraft.newAircraftRequest.messages.uploadTimeframe')}
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {success ? (
            <div />
          ) : (
            <>
              <Button color="error" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!Boolean(make) || !Boolean(model)}
                color="success"
                variant="contained"
                onClick={async () => {
                  await onSubmit();
                  setSuccess(true);
                }}
              >
                {t('common.submit')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

type Props = {
  label: string;
  onSelect: (a: Aircraft) => void;
  defaultSelected?: Aircraft;
  shouldFlash?: boolean;
  /** Initial search text to pre-fill the input (e.g., from FlightBridge type hint) */
  initialSearchText?: string;
  /** Auto-open the dropdown when mounted with initialSearchText */
  autoOpen?: boolean;
  /**
   * Pre-matched aircraft to highlight when dropdown opens.
   * When provided with autoOpen=true, this aircraft will be pre-selected/highlighted
   * so user can confirm with a single click.
   */
  defaultHighlightedMatch?: Aircraft;
  /** Called when the autocomplete is dismissed (blur without selection) */
  onDismiss?: () => void;
  /**
   * Disable portal for the dropdown (renders inline instead of in document.body).
   * Useful when wrapping with ClickAwayListener - without this, clicks on dropdown
   * options are seen as "outside" the listener.
   */
  disablePortal?: boolean;
};

export const AutocompleteAircraft: React.FC<Props> = ({
  label,
  onSelect,
  defaultSelected,
  shouldFlash = false,
  initialSearchText = '',
  autoOpen = false,
  defaultHighlightedMatch,
  onDismiss,
  disablePortal = false,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const [submitNewAircraftOpen, setSubmitNewAircraftOpen] =
    React.useState<boolean>(false);
  const [selected, setSelected] = React.useState<Aircraft>(defaultSelected);
  const [isHighlighted, setIsHighlighted] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState<string>(initialSearchText);
  // Don't auto-open immediately - wait for first render to ensure inputValue is applied
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const shouldAutoOpen = React.useRef(autoOpen && !!initialSearchText);
  // Track which option should be highlighted in the dropdown
  const [highlightedOption, setHighlightedOption] = React.useState<Aircraft | null>(
    defaultHighlightedMatch || null
  );
  const isFirstRender = React.useRef(true);
  const flashedAircraftId = React.useRef<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const { postgrest } = useApi();
  const state = useAircraftData();

  // When dropdown opens with a defaultHighlightedMatch, set it as the highlighted option
  React.useEffect(() => {
    if (isOpen && defaultHighlightedMatch && !highlightedOption) {
      setHighlightedOption(defaultHighlightedMatch);
    }
  }, [isOpen, defaultHighlightedMatch, highlightedOption]);

  // Update selected only when defaultSelected is explicitly provided and differs
  React.useEffect(() => {
    // Only sync from defaultSelected when it's actually provided (not undefined)
    // This prevents clearing user selections when defaultSelected is not used
    if (defaultSelected && defaultSelected.id !== selected?.id) {
      // Extract ID before type guard to avoid TypeScript narrowing issues
      const aircraftId = defaultSelected.id;

      // Only use defaultSelected if it has full geometry data
      // Otherwise, fetch the full data first to prevent partial data issues
      if (isFullAircraft(defaultSelected)) {
        setSelected(defaultSelected);
      } else if (aircraftId) {
        // Fetch full aircraft data before setting (defaultSelected may be partial)
        fetchAircraft(postgrest, aircraftId, activeFBO).then((fullAircraft) => {
          if (fullAircraft) {
            setSelected(fullAircraft);
          }
        });
      }
    }
  }, [defaultSelected, selected?.id, postgrest, activeFBO]);

  // Flash when shouldFlash is true and we have a NEW aircraft to show
  React.useEffect(() => {
    if (shouldFlash && defaultSelected?.id) {
      // Only flash if this is a different aircraft than we've already flashed
      if (flashedAircraftId.current !== defaultSelected.id) {
        flashedAircraftId.current = defaultSelected.id;
        setIsHighlighted(true);
        // Reset highlight after 1 second
        const timer = setTimeout(() => {
          setIsHighlighted(false);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [shouldFlash, defaultSelected?.id]);

  // Track first render separately
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  // Auto-open dropdown after mount (ensures inputValue is applied to filtering first)
  React.useEffect(() => {
    if (shouldAutoOpen.current) {
      // Small delay to ensure the inputValue state is fully applied
      const timer = setTimeout(() => {
        setIsOpen(true);
        shouldAutoOpen.current = false;
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Cleanup: abort any pending requests on unmount
  React.useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <>
      <Autocomplete
        loading={state.loading}
        options={state.value ?? []}
        filterOptions={filterOptions}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={(_, reason) => {
          setIsOpen(false);
          // Call onDismiss when user clicks away or presses Escape without selecting
          if (reason === 'blur' || reason === 'escape') {
            onDismiss?.();
          }
        }}
        blurOnSelect
        disablePortal={disablePortal}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          // Always update inputValue - MUI uses reason='reset' after selection
          // to sync the input text with the selected option's label
          setInputValue(newInputValue);
        }}
        ListboxProps={{
          // Limit visible height and optimize scrolling
          style: {
            maxHeight: "40vh", // Limit to 40% of viewport height
            overflow: "auto",
          },
        }}
        slotProps={{
          popper: {
            sx: {
              zIndex: 10000, // Higher than dialog
            },
          },
        }}
        // Custom option rendering to highlight pre-matched aircraft
        renderOption={(props, option) => {
          const isPreMatched = defaultHighlightedMatch?.id === option.id;
          return (
            <li
              {...props}
              key={option.id}
              style={{
                ...props.style,
                backgroundColor: isPreMatched ? 'rgba(99, 91, 255, 0.12)' : undefined,
                fontWeight: isPreMatched ? 600 : undefined,
              }}
            >
              {option.make} {option.model}
              {isPreMatched && (
                <span style={{ marginLeft: 8, color: '#635bff', fontSize: 11 }}>
                  {t('settings.hangarSettings.autocompleteAircraft.suggested')}
                </span>
              )}
            </li>
          );
        }}
        noOptionsText={
          <Button
            variant="contained"
            color="success"
            endIcon={<HelpOutlineIcon />}
            onClick={() => setSubmitNewAircraftOpen(true)}
          >
            {t('settings.hangarSettings.autocompleteAircraft.noOptionsButton')}
          </Button>
        }
        getOptionLabel={(option: Aircraft) => {
          if (!option) return "";
          return `${option.make || ""} ${option.model || ""}`.trim();
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={async (event: any, newValue: Aircraft | null) => {
          // Cancel any in-flight request to prevent race conditions
          abortControllerRef.current?.abort();
          abortControllerRef.current = new AbortController();
          const currentAbortController = abortControllerRef.current;

          if (!newValue) {
            onSelect(null);
            setSelected(null);
            return;
          }

          // Show visual selection immediately (just for the text field display)
          // DON'T call onSelect yet - wait for full data to prevent partial data issues
          setSelected(newValue);

          try {
            // Use existing fetchAircraft() - it handles caching and returns full Aircraft
            const fullAircraft = await fetchAircraft(
              postgrest,
              newValue.id,
              activeFBO
            );

            // Check if this request was superseded by a newer selection
            if (currentAbortController.signal.aborted) return;

            if (!fullAircraft) {
              throw new Error("Aircraft not found");
            }

            // NOW call onSelect with complete data
            onSelect(fullAircraft);
            setSelected(fullAircraft);
          } catch (error) {
            // Ignore abort errors - they're expected when user selects a new aircraft
            if (currentAbortController.signal.aborted) return;
            console.error("Failed to load aircraft details:", error);
            // Clear selection on error - don't leave partial data
            setSelected(null);
            onSelect(null);
          }
        }}
        value={selected || null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? t('settings.hangarSettings.autocompleteAircraft.defaultLabel')}
            size="small"
            sx={(theme) => ({
              "& .MuiOutlinedInput-root": isHighlighted
                ? {
                    borderColor: theme.palette.success.main,
                    borderWidth: "2px",
                    boxShadow: `0 0 0 3px ${theme.palette.success.main}40`,
                    animation: "highlight-pulse 1s ease-in-out 1",
                    "@keyframes highlight-pulse": {
                      "0%": {
                        borderColor: theme.palette.success.main,
                        borderWidth: "2px",
                        boxShadow: `0 0 0 3px ${theme.palette.success.main}40`,
                      },
                      "50%": {
                        borderColor: theme.palette.success.light,
                        borderWidth: "3px",
                        boxShadow: `0 0 0 6px ${theme.palette.success.main}60`,
                      },
                      "100%": {
                        borderColor: theme.palette.success.main,
                        borderWidth: "2px",
                        boxShadow: `0 0 0 3px ${theme.palette.success.main}40`,
                      },
                    },
                  }
                : {},
            })}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              "data-testid": "autocomplete_aircraft",
            }}
          />
        )}
      />
      <SubmitNewAircraftForm
        open={submitNewAircraftOpen}
        setOpen={setSubmitNewAircraftOpen}
      />
    </>
  );
};
